import { Link } from "@tanstack/react-router";
import { Twitter, Instagram, Linkedin, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Logo } from "@/components/brand/logo";

export function SiteFooter() {
  const { t } = useTranslation();
  const cols: { title: string; links: { label: string; to?: string; href?: string }[] }[] = [
    {
      title: t("footer.explore"),
      links: [
        { label: t("footer.discoverSpaces"), to: "/discover" },
        { label: t("footer.categories"), to: "/categories" },
        { label: t("footer.listYourSpace"), to: "/partner" },
      ],
    },
    {
      title: t("footer.hosts"),
      links: [
        { label: t("footer.listYourSpace"), to: "/partner" },
        { label: t("footer.pricing"), to: "/pricing" },
        { label: t("footer.calculateEarnings"), to: "/calculator" },
      ],
    },
    {
      title: t("footer.company"),
      links: [
        { label: t("footer.about"), to: "/about" },
        { label: t("footer.contact"), to: "/contact" },
        { label: t("footer.helpCenter"), to: "/help" },
      ],
    },
    {
      title: t("footer.legal"),
      links: [
        { label: t("footer.privacy"), to: "/privacy" },
        { label: t("footer.terms"), to: "/terms" },
        { label: t("footer.cancellation"), to: "/cancellation" },
      ],
    },
  ];

  return (
    <footer className="border-t border-border bg-surface">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="flex items-center">
              <Logo className="h-8 w-auto text-foreground" />
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-muted-foreground">
              {t("footer.tagline")}
            </p>

            <a
              href="mailto:hello@spayzo.app"
              className="mt-6 inline-flex items-center gap-2 text-sm text-foreground hover:text-primary"
            >
              <Mail className="h-4 w-4" />
              <span>hello@spayzo.app</span>
            </a>

            <div className="mt-6 flex gap-2">
              {[Twitter, Instagram, Linkedin].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  aria-label="Social link"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4 lg:col-span-8">
            {cols.map((col) => (
              <div key={col.title}>
                <div className="text-xs font-semibold uppercase tracking-widest text-foreground">{col.title}</div>
                <ul className="mt-4 space-y-3">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      {link.to ? (
                        <Link
                          to={link.to}
                          className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                        >
                          {link.label}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                        >
                          {link.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-border pt-8 text-xs text-muted-foreground sm:flex-row sm:items-center">
          <div>© {new Date().getFullYear()} Spayzo, Inc. {t("footer.rights")}</div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-foreground">
              {t("footer.privacy")}
            </Link>
            <Link to="/terms" className="hover:text-foreground">
              {t("footer.terms")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
